import { Link } from "react-router-dom";
import { usePageMeta } from "@/hooks/usePageMeta";
import Breadcrumb from "@/components/feature/Breadcrumb";

const scopes = [
  {
    icon: "ri-film-line",
    title: "伦理电影",
    desc: "聚焦家庭、婚姻与人性的剧情长片，以完整篇幅呈现复杂的人物关系与道德困境。",
  },
  {
    icon: "ri-live-line",
    title: "伦理电视剧",
    desc: "细腻刻画亲密关系与都市情感的连续剧集，在漫长叙事中勾勒人物成长与选择。",
  },
  {
    icon: "ri-clapperboard-line",
    title: "伦理短片",
    desc: "以精炼篇幅直击主题的短片作品，短小却余韵悠长，适合碎片时间观看。",
  },
];

const principles = [
  { icon: "ri-shield-check-line", title: "资料严谨", desc: "作品元信息经过整理核对，导演、主演、年份等字段清晰准确。" },
  { icon: "ri-layout-masonry-line", title: "浏览便捷", desc: "多维度筛选与排序，帮助你在众多作品中快速定位想看的内容。" },
  { icon: "ri-heart-3-line", title: "收藏随心", desc: "支持本地收藏，随时标记感兴趣的作品，记录你的观影清单。" },
  { icon: "ri-device-line", title: "多端适配", desc: "桌面与手机端均精心优化，随时随地顺畅浏览。" },
];

export default function About() {
  usePageMeta(
    "关于我们 - 伦理片在线观看",
    "了解伦理片在线观看平台的定位、内容范围与建设宗旨。我们聚合伦理电影、电视剧及短片资料，打造高品质、便捷、沉浸的观影平台。",
  );

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 md:px-6 md:py-10">
      <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />

      <header className="mt-5 overflow-hidden rounded-lg bg-primary-800 px-6 py-10 md:px-10 md:py-14">
        <span className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-2.5 py-1 text-xs font-medium text-background-50">
          <i className="ri-information-line"></i>
          ABOUT US
        </span>
        <h1 className="mt-4 font-heading text-3xl font-bold text-background-50 md:text-4xl">
          关于伦理片在线观看
        </h1>
        <p className="mt-4 max-w-3xl text-sm leading-7 text-primary-100 md:text-base">
          伦理片在线观看是一个专注于伦理类影视作品的信息聚合平台。我们希望为喜爱伦理题材的观众提供一个高质量、易浏览的作品资料库，让发现好作品这件事变得简单而愉悦。
        </p>
      </header>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">平台定位</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-lg font-semibold text-primary-700">面向的用户</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              我们服务于喜爱伦理题材影视作品的成人观众，他们追求高质量的伦理影视资源，也希望拥有便捷、直观的浏览与筛选体验。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <h3 className="font-heading text-lg font-semibold text-primary-700">核心价值</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-700">
              通过清晰的分类、多维度筛选与详尽的作品资料，帮助用户快速了解一部作品的基本信息与剧情梗概，并发现更多相关内容。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">内容范围</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 md:grid-cols-3">
          {scopes.map((item, i) => (
            <div key={item.title} className="rounded-lg border border-background-200 bg-background-50 p-6">
              <span
                className={`flex h-12 w-12 items-center justify-center rounded-lg ${
                  i === 1 ? "bg-accent-500 text-background-50" : "bg-primary-500 text-background-50"
                }`}
              >
                <i className={`${item.icon} text-2xl`}></i>
              </span>
              <h3 className="mt-4 font-heading text-lg font-semibold text-foreground-950">
                {item.title}
              </h3>
              <p className="mt-2 text-sm leading-7 text-foreground-600">{item.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">我们的坚持</h2>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          {principles.map((item, i) => (
            <div
              key={item.title}
              className="flex gap-4 rounded-lg border border-background-200 bg-background-50 p-5"
            >
              <span
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-md ${
                  i % 2 === 0 ? "bg-primary-50 text-primary-600" : "bg-accent-50 text-accent-600"
                }`}
              >
                <i className={`${item.icon} text-xl`}></i>
              </span>
              <div>
                <h3 className="font-heading text-base font-semibold text-foreground-950">
                  {item.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-foreground-600">{item.desc}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-background-100 p-6 md:p-8">
        <h2 className="font-heading text-2xl font-bold text-foreground-950">团队与联系</h2>
        <div className="mt-4 grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
              <i className="ri-team-line"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">编辑团队</h3>
              <p className="mt-1 text-sm text-foreground-600">
                由多名影视资料编辑共同维护，持续整理与更新作品信息。
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
              <i className="ri-mail-line"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">联系邮箱</h3>
              <p className="mt-1 text-sm text-foreground-600">service@ethicsfilm.example</p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-background-50 text-primary-600">
              <i className="ri-map-pin-line"></i>
            </span>
            <div>
              <h3 className="text-sm font-semibold text-foreground-900">办公地址</h3>
              <p className="mt-1 text-sm text-foreground-600">中国 · 上海市静安区文化创意产业园</p>
            </div>
          </div>
        </div>
        <Link
          to="/contact"
          className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
        >
          前往联系我们
          <i className="ri-arrow-right-line"></i>
        </Link>
      </section>

      <section className="mt-10 rounded-lg border border-background-200 bg-background-50 p-6 md:p-8">
        <h2 className="flex items-center gap-2.5 font-heading text-xl font-bold text-foreground-950">
          <span className="flex h-8 w-8 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
            <i className="ri-copyright-line"></i>
          </span>
          版权声明
        </h2>
        <div className="mt-4 space-y-3 text-sm leading-7 text-foreground-600">
          <p>
            本站所有页面展示的作品信息（含片名、简介、演职人员等）均为资料性内容，仅供观众学习、研究与交流使用。
          </p>
          <p>
            本站不提供任何影音文件的存储、上传与下载服务。如相关内容涉及版权问题，请通过联系页面与我们取得联系，我们将及时处理。
          </p>
          <p>© 2026 伦理片在线观看. All rights reserved.</p>
        </div>
      </section>
    </div>
  );
}